import type {
  AccessRequestResponse,
  AccessRequestReviewed,
  BatchArgs,
  BatchResponse,
  CRMOrganization,
  IDemo,
  IOrganizationData,
  IRecording,
  OrganizationPlan,
  OrganizationStatus,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { handleBatchRequest } from '../utils';
import { mainApi } from './mainApi';

Debug.extend('demostack-data:api:adminApi');

export interface SetAllowedHostsArgs {
  allowedHosts: string[];
  id: string;
}

export interface AssignRoleToUserArgs {
  email: string;
  organizationId: number;
  role: string;
}

export interface ImportInAppDynamicDemoArgs {
  organizationId: number;
  demoName: string;
  sourceDemoId: number;
  sourceDemoOrganizationId: number;
  sourceDemoRevisionId: number;
  targetWorkspaceId: number | null;
}

export interface ImportInAppDynamicRecordingArgs {
  destinationOrganization: number;
  recordName: string;
  description: string;
  sourceRecordId: string;
  sourceRecordOrganization: number;
  targetWorkspaceId: number | null;
}

export interface ImportCloneAsRevisionArgs {
  destinationOrganization: number;
  sourceRecordId: number;
  sourceRecordOrganization: number;
  targetRecordId: number;
  revisionDescription: string;
}

export interface SetOrganizationStatusArgs {
  status?: OrganizationStatus;
  active?: boolean;
  sfAccount?: string;
  organizationId: number;
  planId?: OrganizationPlan;
}

export interface ExportNewDynamicEntityArgs {
  entityId: string;
  entityRevisionId: string;
  entityType: string;
  organizationId: string;
}

export interface ImportNewDynamicEntityArgs {
  organizationId: string;
  key: string;
  fileUrl: string;
  workspaceId: number | null;
}

export interface ExportNewDynamicEntityResponse {
  fileUrl: string;
}

export interface RequestOrganizationAccessArgs {
  accessDuration: number;
  organizationId: number;
  reason: string;
  role: AccessRequestResponse['role'];
}

export interface ManageAccessRequestArgs {
  id: number;
  decision: 'approved' | 'denied';
}

export interface DeleteAccessRequestArgs {
  id: number;
}

export interface StudioAccessArgs {
  organizationId: string;
  recordingId: string;
}

export interface StudioAccessResponse {
  recorderStudioUrl: 'string';
}

export interface SetOrganizationPurgeArgs {
  organizationId: string;
}

export interface UploadAssetArgs {
  organizationId: string;
  file: File;
}

export interface GetSignedFileUrlResponse {
  uploadUrl: string;
}
export interface GetSignedFileUrlArgs {
  organizationId: string;
  uploadAssetType: 'file' | 'audio' | 'video';
  fileName?: string;
  extension?: string;
}

interface DownloadIntegrationReportResponse {
  signedUrl: string;
}

type ImportTemplateContentParams = {
  templateOrganizationId: string;
  organizationId: string;
  includeAnalyticsData?: boolean;
};

type SearchCRMOrganizationsArgs = BatchArgs & {
  query: string;
};

const adminApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    assignRoleToUser: builder.mutation<unknown, AssignRoleToUserArgs>({
      query: ({ email, organizationId, role }) => ({
        url: '/api/roles/new',
        method: 'PUT',
        body: {
          email,
          organizationId: Number(organizationId),
          role,
        },
      }),
    }),

    getAdminOrganizations: builder.query<IOrganizationData[], void>({
      query: () => ({
        url: '/api/admin/utils/organizations',
        method: 'GET',
      }),
      providesTags: ['AdminOrganizations'],
    }),

    setAllowedHosts: builder.mutation<IOrganizationData, SetAllowedHostsArgs>({
      query: ({ id, allowedHosts }) => ({
        url: `/api/admin/${id}/allowed-hosts`,
        method: 'POST',
        body: { allowedHosts },
      }),
      invalidatesTags: ['AdminOrganizations'],
    }),

    importInAppDynamicDemo: builder.mutation<IDemo, ImportInAppDynamicDemoArgs>(
      {
        query: ({
          demoName,
          sourceDemoId,
          sourceDemoOrganizationId,
          sourceDemoRevisionId,
          organizationId,
          targetWorkspaceId,
        }) => ({
          url: `/api/admin/utils/dynamic/in_app/import/demo/${organizationId}`,
          method: 'PUT',
          body: {
            demoName,
            sourceDemoId,
            sourceDemoOrganization: sourceDemoOrganizationId,
            sourceDemoRevisionId,
            targetWorkspaceId,
          },
        }),
        invalidatesTags: ['AdminOrganizations'],
      }
    ),

    importInAppDynamicRecording: builder.mutation<
      IRecording,
      ImportInAppDynamicRecordingArgs
    >({
      query: ({
        destinationOrganization,
        recordName,
        description,
        sourceRecordId,
        sourceRecordOrganization,
        targetWorkspaceId,
      }) => ({
        url: `/api/admin/utils/dynamic/in_app/import/${destinationOrganization}`,
        method: 'PUT',
        body: {
          recordName,
          description,
          sourceRecordId,
          sourceRecordOrganization,
          targetWorkspaceId,
        },
      }),
      invalidatesTags: ['AdminOrganizations'],
    }),

    importCloneAsRevision: builder.mutation<
      IRecording,
      ImportCloneAsRevisionArgs
    >({
      query: ({
        destinationOrganization,
        revisionDescription,
        targetRecordId,
        sourceRecordId,
        sourceRecordOrganization,
      }) => ({
        url: `/api/admin/utils/dynamic/in_app/import/${destinationOrganization}`,
        method: 'POST',
        body: {
          sourceRecordId,
          sourceRecordOrganization,
          revisionDescription,
          targetRecordId,
        },
      }),
      invalidatesTags: (_, __, { targetRecordId }) => [
        { type: 'AdminOrganizations' },
        { type: 'Recording', id: targetRecordId },
      ],
    }),

    setOrganizationStatus: builder.mutation<
      IOrganizationData,
      SetOrganizationStatusArgs
    >({
      query: ({ organizationId, status, active, sfAccount, planId }) => ({
        url: `/api/admin/${organizationId}/status`,
        method: 'POST',
        body: {
          state: status,
          active,
          sfAccount,
          planId,
        },
      }),
      invalidatesTags: (req) => [
        'AdminOrganizations',
        'Organizations',
        { type: 'Organization', id: req.id },
      ],
    }),

    exportNewDynamicEntity: builder.mutation<
      ExportNewDynamicEntityResponse,
      ExportNewDynamicEntityArgs
    >({
      query: ({ organizationId, entityId, entityRevisionId, entityType }) => ({
        url: '/api/admin/utils/dynamic/transfer/export',
        method: 'POST',
        body: {
          organizationId,
          entityId,
          entityRevisionId,
          entityType,
        },
      }),
    }),

    importNewDynamicEntity: builder.mutation<
      IRecording,
      ImportNewDynamicEntityArgs
    >({
      query: ({ organizationId, key, fileUrl, workspaceId }) => ({
        url: '/api/admin/utils/dynamic/transfer/import',
        method: 'POST',
        body: {
          organizationId,
          key,
          fileUrl,
          workspaceId,
        },
      }),
    }),

    requestOrgAccess: builder.mutation<
      AccessRequestResponse,
      RequestOrganizationAccessArgs
    >({
      query: (args) => ({
        url: '/api/admin/access-request/new',
        method: 'PUT',
        body: { ...args },
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'HandledAdminAccessRequests' },
              { type: 'PendingAdminAccessRequests' },
              { type: 'Organizations' },
              { type: 'User' },
            ]
          : [
              { type: 'HandledAdminAccessRequests' },
              { type: 'PendingAdminAccessRequests' },
            ],
    }),

    getRequestsHistory: builder.query<
      BatchResponse<AccessRequestReviewed>,
      BatchArgs
    >({
      query: (args) =>
        handleBatchRequest({
          ...args,
          url: '/api/admin/access-requests/handled',
        }),
      providesTags: ['HandledAdminAccessRequests'],
    }),

    getRequestsPending: builder.query<
      BatchResponse<AccessRequestResponse>,
      BatchArgs
    >({
      query: (args) =>
        handleBatchRequest({
          ...args,
          url: '/api/admin/access-requests/pending',
        }),
      providesTags: ['PendingAdminAccessRequests'],
    }),

    manageAccessRequest: builder.mutation<
      AccessRequestReviewed,
      ManageAccessRequestArgs
    >({
      query: ({ id, decision }) => ({
        url: `/api/admin/access-request/${id}`,
        method: 'POST',
        body: { decision },
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'PendingAdminAccessRequests' },
              { type: 'HandledAdminAccessRequests' },
              { type: 'Organizations' },
              { type: 'User' },
            ]
          : [
              { type: 'PendingAdminAccessRequests' },
              { type: 'HandledAdminAccessRequests' },
            ],
    }),

    disableAccessRequest: builder.mutation<
      AccessRequestReviewed,
      DeleteAccessRequestArgs
    >({
      query: ({ id }) => ({
        url: `/api/admin/access-request/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: 'PendingAdminAccessRequests' },
        { type: 'HandledAdminAccessRequests' },
      ],
    }),

    askStudioAccess: builder.mutation<StudioAccessResponse, StudioAccessArgs>({
      query: ({ organizationId, recordingId }) => ({
        url: '/api/admin/recorder-studio/access',
        method: 'POST',
        body: { organizationId, recordingId },
      }),
    }),

    setOrganizationPurge: builder.mutation<
      IOrganizationData,
      SetOrganizationPurgeArgs
    >({
      query: ({ organizationId }) => ({
        url: `/api/admin/${organizationId}/purge/mark`,
        method: 'POST',
      }),
      invalidatesTags: ['AdminOrganizations', 'Organizations'],
    }),

    getSignedFileUrl: builder.query<
      GetSignedFileUrlResponse,
      GetSignedFileUrlArgs
    >({
      query: ({ organizationId, uploadAssetType, fileName, extension }) => {
        return {
          url: `/api/admin/utils/${organizationId}/fs-upload`,
          method: 'GET',
          params: {
            uploadAssetType,
            fileName,
            extension,
          },
        };
      },
    }),

    downloadIntegrationReport: builder.query<
      DownloadIntegrationReportResponse,
      void
    >({
      query: () => ({
        url: '/api/admin/utils/integrations-report',
        method: 'GET',
      }),
    }),

    importTemplateContent: builder.mutation<void, ImportTemplateContentParams>({
      query: ({
        organizationId,
        templateOrganizationId,
        includeAnalyticsData = false,
      }) => ({
        url: `/api/admin/${organizationId}/import/template`,
        method: 'POST',
        params: {
          templateOrganizationId,
          includeAnalyticsData,
        },
      }),
    }),
    searchCRMOrganizations: builder.query<
      BatchResponse<CRMOrganization>,
      SearchCRMOrganizationsArgs
    >({
      query: ({ query }) =>
        handleBatchRequest({
          method: 'GET',
          url: '/api/admin/crm/companies',
          params: removeUndefinedValues({ query }),
        }),
    }),
  }),
});

export const {
  useAssignRoleToUserMutation,
  useGetAdminOrganizationsQuery,
  useLazyGetAdminOrganizationsQuery,
  useSetAllowedHostsMutation,
  useImportInAppDynamicDemoMutation,
  useImportInAppDynamicRecordingMutation,
  useImportCloneAsRevisionMutation,
  useSetOrganizationStatusMutation,
  useExportNewDynamicEntityMutation,
  useImportNewDynamicEntityMutation,
  useRequestOrgAccessMutation,
  useGetRequestsHistoryQuery,
  useGetRequestsPendingQuery,
  useLazyGetRequestsHistoryQuery,
  useLazyGetRequestsPendingQuery,
  useManageAccessRequestMutation,
  useDisableAccessRequestMutation,
  useAskStudioAccessMutation,
  useSetOrganizationPurgeMutation,
  useLazyGetSignedFileUrlQuery,
  useLazyDownloadIntegrationReportQuery,
  useImportTemplateContentMutation,
  useLazySearchCRMOrganizationsQuery,
} = adminApi;
